import { useCallback, useEffect, useRef, useState } from 'react'

type UseInstantQueryOptions<T> = {
  queryFn: () => Promise<T | null>
  enabled?: boolean
  /** Re-run when these change (e.g., ids, filters). Keep it stable. */
  deps?: React.DependencyList
}

export const useInstantQuery = <T>(options: UseInstantQueryOptions<T>) => {
  const { queryFn, enabled = true, deps = [] } = options

  // Keep latest queryFn without re-triggering the effect on every new identity
  const latestFn = useRef(queryFn)
  useEffect(() => {
    latestFn.current = queryFn
  }, [queryFn])

  const [data, setData] = useState<T | null>(null)
  const [isPending, setIsPending] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const refetch = useCallback(async (signal?: AbortSignal) => {
    setIsPending(true)
    try {
      const result = await latestFn.current()
      if (signal?.aborted) return
      setData(result)
      setError(null)
    } catch (e) {
      if ((e as any)?.name === 'AbortError') return
      setError(e as Error)
    } finally {
      if (!signal?.aborted) setIsPending(false)
    }
  }, [])

  useEffect(() => {
    if (!enabled) return
    const ac = new AbortController()
    refetch(ac.signal)
    return () => ac.abort() // cancel stale requests
    // Only re-run when enabled toggles or your explicit deps change
  }, [enabled, refetch, ...deps])

  return { data, isPending, error, refetch }
}
